import { useQuery } from "@tanstack/react-query";
import { Search, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { api } from "../lib/api";

type SystemUser = {
  id: string;
  displayName: string;
  email: string;
  locale: string;
  status: string;
  platformRoles: string[];
  createdAt: string | null;
  updatedAt: string | null;
  deletionRequestedAt: string | null;
  workspaces: Array<{
    id: string;
    name: string;
    status: string;
    plan: string;
    roles: string[];
    membershipStatus: string;
    joinedAt: string | null;
  }>;
};

export function SystemUsersSection() {
  const [search, setSearch] = useState("");
  const query = useQuery({
    queryKey: ["admin-system-users"],
    queryFn: () => api<{ data: { items: SystemUser[]; total: number } }>("/admin/users"),
  });
  const users = query.data?.data.items ?? [];
  const filteredUsers = useMemo(() => {
    const term = normalizeSearch(search);
    if (!term) return users;
    return users.filter((user) => normalizeSearch([
      user.displayName,
      user.email,
      ...user.platformRoles,
      ...user.workspaces.flatMap((workspace) => [workspace.name, workspace.plan, ...workspace.roles]),
    ].join(" ")).includes(term));
  }, [search, users]);
  const workspaceCount = new Set(users.flatMap((user) => user.workspaces.map((workspace) => workspace.id))).size;

  return (
    <>
      <header className="settings-heading">
        <span><ShieldCheck size={21} /></span>
        <div><h2>Usuários do Sistema</h2><p>Contas registradas em todos os workspaces da plataforma.</p></div>
      </header>
      <div className="admin-metrics">
        <Metric label="Usuários cadastrados" value={query.data?.data.total ?? "—"} />
        <Metric label="Contas ativas" value={query.data ? users.filter((user) => user.status === "active" && !user.deletionRequestedAt).length : "—"} />
        <Metric label="Workspaces" value={query.data ? workspaceCount : "—"} />
        <Metric label="Administradores da plataforma" value={query.data ? users.filter((user) => user.platformRoles.includes("platform_admin")).length : "—"} />
      </div>
      <div className="settings-card admin-users-card">
        <div className="admin-users-toolbar">
          <label className="admin-users-search">
            <Search size={17} />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nome, e-mail ou workspace"
              aria-label="Buscar usuários do sistema"
            />
          </label>
          <span>{search ? `${filteredUsers.length} de ${users.length}` : `${users.length} usuário${users.length === 1 ? "" : "s"}`}</span>
        </div>
        {query.isPending && <p className="admin-users-state">Carregando usuários…</p>}
        {query.error && <p className="form-error">{query.error.message}</p>}
        {!query.isPending && !query.error && filteredUsers.length === 0 && (
          <p className="admin-users-state">{search ? "Nenhum usuário corresponde à busca." : "Nenhum usuário cadastrado."}</p>
        )}
        {filteredUsers.length > 0 && (
          <div className="admin-users-table-wrap">
            <table className="admin-users-table">
              <thead><tr><th>Usuário</th><th>Workspace e acesso</th><th>Status</th><th>Cadastro</th></tr></thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const accountStatus = user.deletionRequestedAt ? "deletion_requested" : user.status;
                  return (
                    <tr key={user.id}>
                      <td>
                        <div className="admin-user-identity">
                          <span className="admin-user-avatar">{userInitials(user.displayName)}</span>
                          <span><strong>{user.displayName}</strong><small>{user.email}</small>{user.platformRoles.includes("platform_admin") && <em>Admin da plataforma</em>}</span>
                        </div>
                      </td>
                      <td>
                        <div className="admin-user-workspaces">
                          {user.workspaces.map((workspace) => (
                            <span key={`${user.id}-${workspace.id}`}>
                              <strong>{workspace.name}</strong>
                              <small>{formatPlan(workspace.plan)} · {workspace.roles.map(formatRole).join(" · ") || "Sem perfil"}</small>
                            </span>
                          ))}
                          {user.workspaces.length === 0 && <small>Sem workspace associado</small>}
                        </div>
                      </td>
                      <td><span className={`admin-user-status admin-user-status--${accountStatus}`}>{formatStatus(accountStatus)}</span></td>
                      <td><time dateTime={user.createdAt ?? undefined}>{formatDate(user.createdAt)}</time></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return <div><small>{label}</small><strong>{value}</strong></div>;
}

const ROLE_LABELS: Record<string, string> = {
  tenant_admin: "Administrador",
  designer: "Designer",
  catalog_manager: "Catálogo",
  manufacturing_reviewer: "Produção",
};

function formatRole(role: string): string {
  return ROLE_LABELS[role] ?? role.replaceAll("_", " ");
}

function formatPlan(plan: string): string {
  if (plan === "free") return "Gratuito";
  if (plan === "professional") return "Profissional";
  if (plan === "enterprise") return "Empresarial";
  return plan === "unknown" ? "Plano não informado" : plan;
}

function formatStatus(status: string): string {
  if (status === "active") return "Ativo";
  if (status === "deletion_requested") return "Exclusão solicitada";
  if (status === "suspended") return "Suspenso";
  if (status === "inactive") return "Inativo";
  return "Não informado";
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

function normalizeSearch(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

function userInitials(displayName: string): string {
  const initials = displayName.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");
  return initials || "?";
}
